import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/Skeleton";
import { RegisterProgramaForm } from "@/components/forms/programas/RegisterProgramaForm";
import { ProgramaCardGestion } from "@/components/dashboards/intermediario/ProgramaCardGestion";
import { EditProgramaForm } from "@/components/forms/programas/EditProgramaForm";
import type { ProgramaClienteFinal } from "@/interfaces/programa-cliente-final.interface";
import type { EditProgramaInput } from "@/lib/validaciones/editProgramaSchema";
import {
  getProgramasScopeConfig,
  type ProgramasScope,
} from "../config/programasScopeConfig";

interface ProgramasGestionPageProps {
  scope?: ProgramasScope;
}

export function ProgramasGestionPage({
  scope = "owned",
}: ProgramasGestionPageProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [editingPrograma, setEditingPrograma] =
    useState<ProgramaClienteFinal | null>(null);

  const scopeConfig = getProgramasScopeConfig(scope);
  const { clientes, total, isLoading, isFetching } = scopeConfig.useProgramas(
    page,
    limit,
    search,
  );

  const totalPages = scopeConfig.supportsPagination
    ? Math.max(1, Math.ceil(total / limit))
    : 1;

  const editFormData: EditProgramaInput | null = useMemo(() => {
    if (!editingPrograma) return null;
    return {
      id: editingPrograma.id,
      idClienteFinal: editingPrograma.idClienteFinal,
      nombre: editingPrograma.nombre,
      tono: editingPrograma.tono ?? "Tono Neutral / Global",
      rol: editingPrograma.rol ?? "",
      restricciones: editingPrograma.restricciones ?? "",
      formato: editingPrograma.formato ?? "",
      // cupoMaximo: editingPrograma.cupoMaximo ?? 0,
      limiteIntentos: editingPrograma.limiteIntentos ?? undefined,
      fechaInicio: new Date(editingPrograma.fechaInicio),
      fechaFin: new Date(editingPrograma.fechaFin),
      contexto_coach: editingPrograma.contexto_coach ?? null,
      rol_coach: editingPrograma.rol_coach ?? null,
      tarea_coach: editingPrograma.tarea_coach ?? null,
      restricciones_criterios_coach:
        editingPrograma.restricciones_criterios_coach ?? null,
    };
  }, [editingPrograma]);

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-80" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearch(value);
  };

  return (
    <section className="p-3 sm:p-4 md:p-5 space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
        {/* <h2 className="text-2xl font-semibold text-[var(--foreground)]">
          {scopeConfig.title}
        </h2> */}

        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="headerAction">{scopeConfig.buttonLabel}</Button>
            </DialogTrigger>
            <DialogContent size="xxl">
              <DialogHeader>
                <DialogTitle>Registrar programa</DialogTitle>
              </DialogHeader>
              <RegisterProgramaForm
                setOpen={setOpen}
                queryKey={scopeConfig.queryInvalidateKey.map((key) =>
                  String(key),
                )}
              />
            </DialogContent>
          </Dialog>
        </div>
        <div className="w-full sm:flex-1 sm:min-w-[300px] flex justify-stretch sm:justify-end">
          {scopeConfig.supportsSearch && (
            <Input
              placeholder="Buscar programas..."
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              className="max-w-md rounded-full bg-white/80 border border-black/40 shadow-sm focus-visible:ring-[var(--color-focus-ring)]"
            />
          )}
        </div>
      </div>

      {clientes.length === 0 ? (
        <div className="text-center text-muted-foreground">
          No hay programas registrados.
        </div>
      ) : (
        clientes.map((cliente) => (
          <section
            key={cliente.id}
            className="rounded-2xl border border-black/5 bg-[var(--background)] p-6 shadow-lg dark:border-white/10"
          >
            <h3 className="text-xl font-semibold text-[var(--foreground)] mb-4">
              {cliente.nombreEmpresa}
            </h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {cliente.programas.length === 0 ? (
                <div className="col-span-full text-muted-foreground">
                  Sin programas registrados
                </div>
              ) : (
                cliente.programas.map((programa) => (
                  <ProgramaCardGestion
                    key={programa.id}
                    programa={programa}
                    onViewDetails={() => navigate(`detalles/${programa.id}`)}
                    onEdit={() => setEditingPrograma(programa)}
                  />
                ))
              )}
            </div>
          </section>
        ))
      )}

      {scopeConfig.supportsPagination && totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1 || isFetching}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || isFetching}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={!!editingPrograma}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setEditingPrograma(null);
          }
        }}
      >
        <DialogContent size="xxl">
          <DialogHeader>
            <DialogTitle>Editar programa</DialogTitle>
          </DialogHeader>
          {editingPrograma && editFormData ? (
            <EditProgramaForm
              data={editFormData}
              close={() => setEditingPrograma(null)}
              queryKey={scopeConfig.queryInvalidateKey.map((key) =>
                String(key),
              )}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
